import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  ContentChild,
  QueryList,
  TemplateRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { IHeaderConfig } from "../headers-component/headers-component.component";
import {
  NgTemplateTableDirective,
  NgTemplateRowTableDirective,
  NgTemplateRowTableResponseDirective,
  NgTemplateRowGroupTableResponseDirective
} from "../ng-template-table.directive";
import { Content } from "@angular/compiler/src/render3/r3_ast";
import { trigger, transition, style, animate, query, animateChild } from '@angular/animations';
import * as hash from 'object-hash';


/**
 * Implement properties that builds a wa table row.
 *
 * @export
 * @interface IWaTableRow
 */
export interface IWaTableRow {
  propertyName?: string;
  hideMobile?: boolean;
  bindingClassName?: string;
  customClass?: Array<string>;
}

/**
 * Implement properties that builds a wa table config.
 *
 * @export
 * @interface IWaTableConfig
 */
export interface IWaTableConfig {
  hasDetails: boolean;
  data: Array<IWaTableRow>;
}

/**
 * Generic animations for tables.
 *
 * @export
 * @interface AnimationsTriggerMetadata[]
 */
export const animationsTable = [
  trigger('slideInOut', [

    transition(':enter', [
      style({ height: 0, paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, opacity: 0 }),
      animate('300ms ease-in', style({ height: '*', opacity: 1, paddingTop: 20, paddingRight: 10, paddingBottom: 30, paddingLeft: 20, marginRight: 8, marginLeft: 8 }))
    ]),

    transition(':leave', [
      animate('300ms ease-in', style({ height: 0, paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, opacity: 0 }))
    ])
  ]),
  trigger('fade', [
    transition(':enter', [
      style({ opacity: 0 }),
      animate('500ms', style({ opacity: 1 })),
      query('@*', animateChild(), { optional: true }),

    ]),
    transition(':leave', [
      animate('500ms', style({ opacity: 0 })),
      query('@*', animateChild(), { optional: true }),
    ])
  ]),
];
/**
 * Main component to show tables in pages.
 *
 * Facilitate tools to provide sortings, headers, filters...
 *
 * @export
 * @class WaTableComponent
 * @implements {OnInit}
 */
@Component({
  selector: "app-wa-table",
  templateUrl: "./wa-table.component.html",
  styleUrls: ["./wa-table.component.scss"],
})
export class WaTableComponent implements OnInit {

  /**
   * Input callback for get route for row and set it dinamically.
   *
   * @memberof WaTableComponent
   */
  @Input() getRouteForRow: (element:any) => {params:any, route:string}

  /**
   * Template for query columns
   *
   * @type {QueryList<NgTemplateTableDirective>}
   * @memberof WaTableComponent
   */
  @ContentChildren(NgTemplateTableDirective) columns: QueryList<
    NgTemplateTableDirective
  >;

  /**
   * Template for query columns for responsive content
   *
   * @type {QueryList<NgTemplateTableDirective>}
   * @memberof WaTableComponent
   */
  @ContentChildren(NgTemplateRowTableResponseDirective)
  columns_responsive: QueryList<NgTemplateRowTableResponseDirective>;

  /**
   * Template for query rows and add details
   *
   * @type {QueryList<NgTemplateRowTableDirective>}
   * @memberof WaTableComponent
   */
  @ContentChildren(NgTemplateRowTableDirective) rows: QueryList<
    NgTemplateRowTableDirective
  >;

  /**
   * Template for query rows group like records name
   *
   * @type {QueryList<NgTemplateRowTableDirective>}
   * @memberof WaTableComponent
   */
  @ContentChild(NgTemplateRowGroupTableResponseDirective, {static:false}) row_group: any;

  /**
   * Data of the templates.
   *
   * @type {Array<any>}
   * @memberof WaTableComponent
   */
  @Input()
  data: Array<any>;

  /**
   * Input for set a custom message box based on conditions.
   *
   * @type {TemplateRef}
   * @memberof WaTableComponent
   */
  @Input()
  customMsgBox:TemplateRef<any>;

  /**
   * Configuration of the table.
   *
   * @type {IWaTableConfig}
   * @memberof WaTableComponent
   */
  @Input()
  config: IWaTableConfig;

  /**
   * Value for set the table ready.
   *
   * @type {boolean}
   * @memberof WaTableComponent
   */
  @Input()
  showCondition: boolean;

  /**
   * Custom header message.
   *
   * @type {string}
   * @memberof WaTableComponent
   */
  @Input()
  headerMessage: string;
  /**
   * A property for set the No results data when there aren't results.
   * Default to 'No Results'. If not set, it won't show anything
   *
   * @type {string}
   * @memberof WaTableComponent
   */
  @Input()
  showNoResultsMessage: string = "No results";

  /**
   * Input for set if not results.
   *
   * @type {boolean}
   * @memberof WaTableComponent
   */
  @Input()
  showNoResultsMessageCondition: boolean;

  /**
   * Input for define the messsage of error when the data is not an array,
   *
   * @type {string}
   * @memberof WaTableComponent
   */
  @Input()
  showErrorMessage:string = 'An unexpected error occurred. Please try again later';

  /**
   * Input for hide or show load more button.
   *
   * @type {boolean}
   * @memberof WaTableComponent
   */
  @Input()
  areMoreData: boolean;

  /**
   * Input for check if there's data loading.
   *
   * @type {boolean}
   * @memberof WaTableComponent
   */
  @Input()
  isDataLoading: boolean;

  /**
   * Output for check if there's loading more.
   *
   * @memberof WaTableComponent
   */
  @Output()
  loadMore = new EventEmitter();

  /**
   * Headers of the table.
   *
   * @type {Array<IHeaderConfig>}
   * @memberof WaTableComponent
   */
  @Input()
  headers: Array<IHeaderConfig>;

  /**
   * Default sort value if any.
   *
   * @type {string}
   * @memberof WaTableComponent
   */
  @Input()
  defaultSort: string;

  /**
   * Event emitter of the clicked header.
   *
   * @memberof WaTableComponent
   */
  @Output()
  headerClick = new EventEmitter();

  /**
   * Custom sortable function of headers click.
   *
   * @memberof WaTableComponent
   */
  @Input()
  isSortableFunc: (name: string) => string;

  @Input()
  extraClass: Array<string> = [];

  /**
   * The event emitter of subheaders.
   *
   * @memberof WaTableComponent
   */
  @Output()
  subHeaderClick = new EventEmitter();

  /**
   * Animation state of the table expand details.
   *
   * @memberof WaTableComponent
   */
  animationDetailsState = 'in';

  /**
   * A input that could be used for generate custom classes in tbody.
   * It's a callback that receives a element and returns strings with classes.
   *
   * @memberof WaTableComponent
   */
  @Input()
  customClassesForTBody?: (element: any) => string = (element: any) => '';

  /**
   * A input that could be used for generate custom classes in tr.
   * It's a callback that receives a element and returns strings with classes.
   *
   * @memberof WaTableComponent
   */
  @Input()
  customClassesForTr?: (element: any) => string = (element: any) => '';
  constructor() { }

  /**
   * Implements ngOnInit().
   *
   * @memberof WaTableComponent
   */
  ngOnInit() {
    if (this.showNoResultsMessage == null) {
      this.showNoResultsMessage = "No results";
    }
  }

  /**
   * Method for check if there's any sortable element and add it to before content of thead.
   *
   * @returns
   * @memberof WaTableComponent
   */
  computeSortHeadersClass(){
    return this.headers.map(value=> value.isSortable).filter(value=> value === true).length > 0
  }

  /**
   * Get classes of an specific row.
   *
   * @param {IWaTableRow} element
   * @returns
   * @memberof WaTableComponent
   */
  getClasses(element: Array<string>) {
    return element.map(value => value).join(" ");
  }

  /**
   * Emit load more event.
   *
   * @memberof WaTableComponent
   */
  loadMoreInner() {
    this.loadMore.emit();
  }

  /**
   * Emit header click event.
   *
   * @param {string} $event
   *   Event name.
   *
   * @memberof WaTableComponent
   */
  headerClickInner($event: string) {
    this.headerClick.emit($event);
  }

  /**
   * Emit subheader click event
   *
   * @param {string} $event
   * @memberof WaTableComponent
   */
  subHeaderClickInner($event: string) {
    this.subHeaderClick.emit($event);
  }

  /**
   * A method for assing an index to a element and be accesible for outside
   *
   * @param {number} index
   * @param {*} element
   * @returns
   * @memberof WaTableComponent
   */
  assignIndex(index: number, element: any) {
    return Object.assign(element, { index: index });
  }

  rowTrackBy(index, item){
    return hash(item);
  }

}
